import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useContext, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Accordion from '../../components/Accordion';
import AccordionBodyItem from '../../components/Accordion/AccordionBodyItem';
import AvatarGroup, {
  AvatarGroupSize,
  AvatarGroupStyle,
} from '../../components/Avatar/AvatarGroup';
import Button from '../../components/Button';
import DefaultCard from '../../components/DefaultCard';
import IconButton from '../../components/IconButton';
import Label from '../../components/Label/Label';
import Popover from '../../components/Popover';
import Switch from '../../components/Switch';
import useModal from '../../hooks/useModal';
import { AuthStore } from '../../providers/auth';
import {
  EnrollmentProvider,
  EnrollmentStore,
} from '../../providers/enrollment';
import { FamilyMembersProvider } from '../../providers/familyMembers';
import {
  addOrRemoveDaysToGivenDate,
  formatDate,
  formatPrice,
  openExternalLink,
  stopVideo,
} from '../../utils/utils';
import DentalBenefitsModal from '../widgets/DentalBenefitsModal/DentalBenefitsModal';
import DependantFsaModal from '../widgets/dependantFSA/DependantFsaModal';
import EnrollmentSuccess from '../widgets/EnrollmentSuccessPage/EnrollmentSuccess';
import HealthFsaModal from '../widgets/healthFSA/HealthFsaModal';
import HealthSavingsModal from '../widgets/healthSavings/HealthSavingsModal';
import HospitalIndemnityModal from '../widgets/hospitalIndemnity/HospitalIndemnityModal';
import LegalServicesModal from '../widgets/legalServices/LegalServicesModal';
import BaseLifeInsuranceModal from '../widgets/lifeAndDisabilityModals/baseLifeInsurance/BaseLifeInsuranceModal';
import LongTermDisabilityServicesModal from '../widgets/lifeAndDisabilityModals/longTermDisabilityServiceModal/LongTermDisabilityServiceModal';
import OptionalLifeInsuranceModal from '../widgets/lifeAndDisabilityModals/optionalLifeInsurance/OptionalLifeInsuranceModal';
import SpouseLifeInsuranceModal from '../widgets/lifeAndDisabilityModals/spouseLifeInsurance/SpouseLifeInsuranceModal';
import MedicalBenefitsModal from '../widgets/MedicalBenefitsModal';
import NonTobaccoUserCreditModal from '../widgets/nonTabaccoUserCredit/NonTabaccoUserCreditModal';
import PopoverWithAlerts from '../widgets/PopoverAlerts/PopoverAlerts';
import SpousalSurchargeModal from '../widgets/spousalSurcharge/SpousalSurchargeModal';
import VideoEmbedModal from '../widgets/VideoEmbedModal/VideoEmbedModal';
import VisionBenefitsModal from '../widgets/VisionBenefitsModal/VisionBenefitsModal';
import CriticalIllnessModal from '../widgets/criticalIllnessModal/criticalIllnessModal';
import PopoverWithLinks from '../widgets/PopoverLinks/PopoverLinks';
import AccidentInsuranceBenefitsModal from '../widgets/AccidentInsuranceModal/AccidentInsuranceModal';

const alertsData = [
  {
    alertDescription: 'Please make an election for Spousal Surcharge',
  },

  {
    alertDescription:
      'Your Optional Life election requires completion of Evidence of insurability (EOI) form and approval from Secrian Financial.',
  },
];

const linksData = [
  {
    linkHeader: 'Benefits Information',
    route: 'benefit-info',
    modal: true,
  },
];

const separateStringAndMakeFirstLetterUppercase = (str: string) => {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getCardIcon = (type: String) => {
  switch (type) {
    case 'heart-pulse':
      return light('heart-pulse');
    case 'tooth':
      return light('tooth');
    case 'glasses':
      return light('glasses');
    case 'face-thermometer':
      return light('face-thermometer');
    case 'family':
      return light('family');
    case 'bed-pulse':
      return light('bed-pulse');
    case 'hospitals':
      return light('hospitals');
    case 'gavel':
      return light('gavel');
    case 'clipboard-medical':
      return light('clipboard-medical');
    default:
      return light('image-slash');
  }
};

const getPlanCostStyle = (type: string) => {
  switch (type) {
    case 'owed':
      return 'default';
    case 'credit':
      return 'green';
    default:
      return 'disabled';
  }
};

const getButtonStyle = (
  type: string,
  alertInfo: any[],
  proceedBtnFunc: any
) => {
  const alertBody = (
    <Col>
      <Row className="pb-7 m-0 fw-normal">
        {alertInfo && alertInfo[0].title}
      </Row>
      <Row className="pb-7 m-0 fw-light lh-xs">
        {alertInfo && alertInfo[0].description}
      </Row>
      <Col className="m-0 d-flex justify-content-end">
        {alertInfo && alertInfo[0].ctaLabel ? (
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              document.body.click();
              proceedBtnFunc();
            }}
          >
            {alertInfo && alertInfo[0].ctaLabel}
          </Button>
        ) : (
          ''
        )}
      </Col>
    </Col>
  );

  switch (type) {
    case 'alert':
      return (
        <Popover placement="top-end" popoverBody={alertBody}>
          <IconButton
            variant="icon-alert"
            icon={light('circle-exclamation')}
            size={'sm'}
          />
        </Popover>
      );
    case 'button':
      return (
        <IconButton
          variant="icon-emphasized"
          icon={light('file-arrow-down')}
          size={'sm'}
          onClick={() =>
            openExternalLink('https://mag.wcoomd.org/uploads/2018/05/blank.pdf')
          }
        ></IconButton>
      );
    default:
      return;
  }
};

const getAlertButton = (alertInfo: any[], proceedBtnFunc: any) => {
  const alertBody = (
    <Col>
      <Row className="pb-7 m-0 fw-normal">
        {alertInfo && alertInfo[0].title}
      </Row>
      <Row className="pb-7 m-0 fw-light lh-xs">
        {alertInfo && alertInfo[0].description}
      </Row>
    </Col>
  );

  return (
    <Popover placement="top-end" popoverBody={alertBody}>
      <IconButton
        variant="icon-alert"
        icon={light('circle-exclamation')}
        size={'sm'}
      />
    </Popover>
  );
};

const getInfoButton = () => {
  return (
    <IconButton
      variant="icon-emphasized"
      icon={light('file-arrow-down')}
      size={'sm'}
      onClick={() =>
        openExternalLink('https://mag.wcoomd.org/uploads/2018/05/blank.pdf')
      }
    ></IconButton>
  );
};

function EnrollmentView({ setSubmitted }: any) {
  const [,] = useContext(AuthStore) || [];
  const {
    currentCost,
    updatedCost,
    activeHealthBenefits,
    activeLifeAndDisabilityInsurances,
    activeLifestyleBenefits,
    activeSpendingAccounts,
    medicalSelectedProfiles,
    dentalSelectedProfiles,
    criticalIlnessSelectedProfiles,
    accidentInsuranceSelectedProfiles,
    visionSelectedProfiles,
    familyMembers,
    addFamilyMember,
  } = useContext(EnrollmentStore) || {};

  const [shouldShowCurrentValue, setShowCurrentValue] =
    useState<boolean>(false);

  const [medicalSelectedPlanRadio, setMedicalSelectedPlanRadio] = useState<
    string | number
  >(1);

  const healthBenefits = useMemo(() => {
    const plans = activeHealthBenefits.filter(
      (e: any) => e.type !== 'health-savings'
    );

    if (medicalSelectedPlanRadio === 0) {
      return activeHealthBenefits;
    }

    return plans;
  }, [activeHealthBenefits, medicalSelectedPlanRadio]);

  const [
    showHealthFSAModal,
    handleShowHealthFSAModal,
    handleCloseHealthFSAModal,
  ] = useModal();
  const [
    showDependedFSAModal,
    handleShowDependedFSAModal,
    handleCloseDependedFSAModal,
  ] = useModal();
  const [
    showSpouseLifeInsuranceModal,
    handleShowSpouseLifeInsuranceModal,
    handleCloseSpouseLifeInsuranceModal,
  ] = useModal();
  const [
    showOptionalLifeInsuranceModal,
    handleShowOptionalLifeInsuranceModal,
    handleCloseOptionalLifeInsuranceModal,
  ] = useModal();
  const [
    showBaseLifeInsuranceModal,
    handleShowBaseLifeInsuranceModal,
    handleCloseBaseLifeInsuranceModal,
  ] = useModal();
  const [
    showLongTermDisabilityServicesModal,
    handleShowLongTermDisabilityServicesModal,
    handleCloseLongTermDisabilityServicesModal,
  ] = useModal();
  const [
    showCriticalIllnessModal,
    handleShowCriticalIllnessModal,
    handleCloseCriticalIllnessModal,
  ] = useModal();
  const [
    showHealthSavingsModal,
    handleShowHealthSavingsModal,
    handleCloseHealthSavingsModal,
  ] = useModal();
  const [
    showSpousalSurchargeModal,
    handleShowSpousalSurchargeModal,
    handleCloseSpousalSurchargeModal,
  ] = useModal();
  const [
    showNonTobaccoUserCreditModal,
    handleShowNonTobaccoUserCreditModal,
    handleCloseNonTobaccoUserCreditModal,
  ] = useModal();
  const [
    showHospitalIndemnityModal,
    handleShowHospitalIndemnityModal,
    handleCloseHospitalIndemnityModal,
  ] = useModal();
  const [
    showLegalServicesModal,
    handleShowLegalServicesModal,
    handleCloseLegalServicesModal,
  ] = useModal();

  const [
    showMedicalBenefitsModal,
    handleShowMedicalBenefitsModal,
    handleCloseMedicalBenefitModal,
  ] = useModal();

  const [
    showDentalBenefitsModal,
    handleShowDentalBenefitsModal,
    handleCloseDentalBenefitsModal,
  ] = useModal();

  const [
    showAccidentInsuranceBenefitsModal,
    handleShowAccidentInsuranceBenefitsModal,
    handleCloseAccidentInsuranceBenefitsModal,
  ] = useModal();

  const [
    showVisionBenefitsModal,
    handleShowVisionBenefitsModal,
    handleCloseVisionBenefitsModal,
  ] = useModal();

  const [
    showVideoEmbedModal,
    handleShowVideoEmbedModal,
    handleCloseVideoEmbedModal,
  ] = useModal();

  const getButtonModal = (type: string) => {
    switch (type) {
      case 'base-life':
        return handleShowBaseLifeInsuranceModal;
      case 'optional-life':
        return handleShowOptionalLifeInsuranceModal;
      case 'spouse-life':
        return handleShowSpouseLifeInsuranceModal;
      case 'long-term-disability':
        return handleShowLongTermDisabilityServicesModal;
      case 'critical-illness':
        return handleShowCriticalIllnessModal;
      case 'health-care':
        return handleShowHealthFSAModal;
      case 'dependant-care':
        return handleShowDependedFSAModal;
      case 'spousal-surcharge':
        return handleShowSpousalSurchargeModal;
      case 'non-tobacco-user-credit':
        return handleShowNonTobaccoUserCreditModal;
      case 'health-savings':
        return handleShowHealthSavingsModal;
      case 'hospital-indemnity':
        return handleShowHospitalIndemnityModal;
      case 'legal-services':
        return handleShowLegalServicesModal;
      case 'medical':
        return handleShowMedicalBenefitsModal;
      case 'dental':
        return handleShowDentalBenefitsModal;
      case 'accident-insurance':
        return handleShowAccidentInsuranceBenefitsModal;
      case 'vision':
        return handleShowVisionBenefitsModal;
    }
  };

  const getActiveProfilesByType = (type: string) => {
    switch (type) {
      case 'medical':
        return medicalSelectedProfiles.flat();
      case 'vision':
        return visionSelectedProfiles.flat();
      case 'dental':
        return dentalSelectedProfiles.flat();
      case 'critical-illness':
        return criticalIlnessSelectedProfiles.flat();  
      case 'accident-insurance':
        return accidentInsuranceSelectedProfiles.flat();
    }
  };

  let shouldHaveHealthBenefitsWarnings = true;
  healthBenefits.forEach((b: any) => {
    if (b['plan']['id'] === 'spc-10000') shouldHaveHealthBenefitsWarnings = false;
  });

  let currentAlerts = [];
  if (!shouldHaveHealthBenefitsWarnings) {
    currentAlerts = [alertsData[1]];
  } else {
    currentAlerts = [...alertsData];
  }

  return (
    <Container className="cnd-custom-container mb-12 px-lg-9 px-xl-0">
      <Row>
        <Col className="mt-10 col-12 col-lg-6">
          <DefaultCard
            className="h-100"
            variant="blue-motif"
            leftHeading={'Benefits Sneak Peek'}
            leftSubheading={`Let's take a quick look into your benefits`}
            button={
              <Button
                className="text-nowrap"
                variant="primary-shadow"
                size="sm"
                rightIcon={light('play')}
                onClick={handleShowVideoEmbedModal}
              >
                Watch Now
              </Button>
            }
          />

          <VideoEmbedModal
            showVideoEmbedModal={showVideoEmbedModal}
            handleCloseVideoEmbedModal={handleCloseVideoEmbedModal}
            stopVideo={stopVideo}
            title={'Benefits Sneak Peek'}
            embeddedNode={
              <div className={'d-flex justify-content-center'}>
                <iframe
                  src={`${process.env.PUBLIC_URL}/${'jellyVision.mp4'}`}
                  title={'rollover'}
                  width={620}
                  height={450}
                ></iframe>
              </div>
            }
          />
        </Col>
        <Col className="d-none d-lg-block col-6 col-lg-3 mt-10">
          <DefaultCard
            variant="white"
            className="h-100"
            leftHeading={formatPrice(currentCost)}
            leftSubheading={`per-pay-period`}
            overlineLabel={
              <Label rightIcon={light('circle-question')}>Current</Label>
            }
            bottomLine={
              <Switch
                variant="sm"
                label={'Show current cost'}
                labelColor="gray"
                id={'show-current-cost'}
                onChange={() => {
                  setShowCurrentValue(!shouldShowCurrentValue);
                }}
                checked={shouldShowCurrentValue}
                name={'show-current-cost'}
              />
            }
          />
        </Col>
        <Col className="col-12 col-lg-3 mt-10">
          <DefaultCard
            variant="white"
            className="h-100"
            leftHeading={formatPrice(updatedCost)}
            leftSubheading={`per-pay-period`}
            overlineLabel={
              <Label rightIcon={light('circle-question')}>New</Label>
            }
            bottomLine={
              <Label className={'text-nowrap p-0'} l>
                Effective Date:{' '}
                {formatDate(addOrRemoveDaysToGivenDate(new Date(), 30))}
              </Label>
            }
          />
        </Col>
      </Row>
      <Row className="mt-12">
        <div className="w-100 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center justify-content-start">
            <PopoverWithAlerts alertsData={currentAlerts} />
          </div>
          <div className="d-flex align-items-center justify-content-end">
            <PopoverWithLinks header='Benefit Info & Add Family Member' linksData={[
              {
                linkHeader: 'Add/View Family Member',
                route: 'view-family-members',
                modal: true,
                params: {
                  addFamilyMember: addFamilyMember, 
                  familyMembers: familyMembers
                },
              },
              ...linksData,
              {
                linkHeader: 'Benefit Options & Cost',
                route: 'benefit-options-and-costs',
                modal: true,
                params: {
                  healthBenefits: activeHealthBenefits,
                  spendingAccounts: activeSpendingAccounts,
                  lifeAndDisabilityInsurances: activeLifeAndDisabilityInsurances,
                  lifestyleBenefits: activeLifestyleBenefits,
                },
              },
            ]} />
          </div>
        </div>
      </Row>
      <Row className="mt-10">
        <Accordion
          title={'Health Benefits'}
          infoIcon={
            shouldHaveHealthBenefitsWarnings
              ? light('circle-exclamation')
              : undefined
          }
          className={'highlight-open'}
        >
          <Container className="custom-accordion-demo heading">
            <Row className="body-rows">
              <Col className="title-container col-lg-1">&nbsp;</Col>
              <Col className="avatars-col col-lg-3">&nbsp;</Col>
              <Col className="avatars-col col-lg-2">&nbsp;</Col>
              <Col className="d-flex p-lg-0 col-lg-2">
                <Col className="price-col me-6 me-lg-0">
                  <span className="d-none d-lg-block text-light-grey-normal text-fs-6 fw-light text-nowrap">
                    {shouldShowCurrentValue ? 'Current Cost' : ''}
                  </span>
                </Col>
                <Col className="price-col">
                  <span className="text-light-grey-normal text-fs-6 fw-light text-nowrap">
                    New Cost
                  </span>
                </Col>
              </Col>
            </Row>
          </Container>
          {healthBenefits.map((benefit: any, index: number) => {
            return (
              <AccordionBodyItem
                key={index}
                infoIcon={getCardIcon(
                  benefit['generic'] && benefit['generic']['icon']
                )}
                title={benefit['generic']['title']}
                subtitle={
                  benefit['plan'] ? benefit['plan']['title'] : 'No Coverage'
                }
                price={
                  benefit['plan']
                    ? formatPrice(Math.abs(benefit['plan']['cost']))
                    : '$0.00'
                }
                originalPrice={
                  benefit['plan']
                    ? formatPrice(Math.abs(benefit['plan']['originalCost']))
                    : '$0.00'
                }
                isOriginalPriceShown={shouldShowCurrentValue}
                priceStyleType={getPlanCostStyle(
                  benefit['plan']['cost'] === 0
                    ? ''
                    : benefit['plan'] && benefit['plan']['costType']
                )}
                originalPriceStyleType={getPlanCostStyle(
                  benefit['plan']['cost'] === 0
                    ? ''
                    : benefit['plan']['originalCost'] < 0
                    ? 'credit'
                    : benefit['plan'] && benefit['plan']['costType']
                )}
                infoButton={
                  benefit['plan']['id'].includes('spc-noCoverage')
                    ? getAlertButton(
                        benefit['alerts'],
                        getButtonModal(benefit['type'])
                      )
                    : benefit['plan']['detailsLink']
                    ? getInfoButton()
                    : ''
                }
                changeButton={
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={getButtonModal(benefit['type'])}
                  >
                    Change
                  </Button>
                }
                isHint={benefit['generic']['hint']}
                avatarGroup={
                  benefit['generic']['familyMembers'] && (
                    <AvatarGroup
                      key={index}
                      avatars={getActiveProfilesByType(benefit['type'])}
                      groupSize={AvatarGroupSize.MINIMIZED}
                      groupStyle={AvatarGroupStyle.STACKED}
                      hasTooltip
                    />
                  )
                }
              />
            );
          })}
          <MedicalBenefitsModal
            radioButtonValue={medicalSelectedPlanRadio}
            setRadioButtonValue={setMedicalSelectedPlanRadio}
            showMedicalBenefitModal={showMedicalBenefitsModal}
            handleCloseMedicalBenefitModal={handleCloseMedicalBenefitModal}
          />
          <DentalBenefitsModal
            showDentalBenefitsModal={showDentalBenefitsModal}
            handleCloseDentalBenefitsModal={handleCloseDentalBenefitsModal}
          />
          <AccidentInsuranceBenefitsModal
            showAccidentInsuranceBenefitsModal={showAccidentInsuranceBenefitsModal}
            handleCloseAccidentInsuranceBenefitsModal={handleCloseAccidentInsuranceBenefitsModal}
          />
          <CriticalIllnessModal
            showCriticalIllnessModal={showCriticalIllnessModal}
            handleCloseCriticalIllnessModal={handleCloseCriticalIllnessModal}
          />
          <HospitalIndemnityModal
            showHospitalIndemnityModal={showHospitalIndemnityModal}
            handleCloseHospitalIndemnityModal={
              handleCloseHospitalIndemnityModal
            }
          />
          <VisionBenefitsModal
            showVisionBenefitsModal={showVisionBenefitsModal}
            handleCloseVisionBenefitsModal={handleCloseVisionBenefitsModal}
          />
          <HealthSavingsModal
            showHealthSavingsModal={showHealthSavingsModal}
            handleCloseHealthSavingsModal={handleCloseHealthSavingsModal}
          />
          <SpousalSurchargeModal
            showSpousalSurchargeModal={showSpousalSurchargeModal}
            handleCloseSpousalSurchargeModal={handleCloseSpousalSurchargeModal}
          />
          <NonTobaccoUserCreditModal
            showNonTobaccoUserCreditModal={showNonTobaccoUserCreditModal}
            handleCloseNonTobaccoUserCreditModal={
              handleCloseNonTobaccoUserCreditModal
            }
          />
        </Accordion>
      </Row>
      <Row className="mt-10">
        <Accordion title={'Spending Accounts'} className={'highlight-open'}>
          <Container className="custom-accordion-demo heading">
            <Row className="body-rows">
              <Col className="title-container col-lg-4">&nbsp;</Col>
              <Col className="avatars-col col-lg-2">&nbsp;</Col>
              <Col className="d-flex p-lg-0 col-lg-2">
                <Col className="price-col me-6 me-lg-0">
                  <span className="d-none d-lg-block text-light-grey-normal text-fs-6 fw-light text-nowrap">
                    {shouldShowCurrentValue ? 'Current Cost' : ''}
                  </span>
                </Col>
                <Col className="price-col">
                  <span className="text-light-grey-normal text-fs-6 fw-light text-nowrap">
                    New Cost
                  </span>
                </Col>
              </Col>
            </Row>
          </Container>
          {activeSpendingAccounts.map((benefit: any, index: number) => (
            <AccordionBodyItem
              key={index}
              infoIcon={getCardIcon(
                benefit['generic'] && benefit['generic']['icon']
              )}
              infoIconVariant={'icon-default'}
              title={
                !benefit['generic']
                  ? separateStringAndMakeFirstLetterUppercase(benefit['type'])
                  : benefit['generic']['title']
              }
              subtitle={
                benefit['plan'] ? benefit['plan']['title'] : 'No Contribution'
              }
              price={
                benefit['plan'] ? formatPrice(benefit['plan']['cost']) : '$0.00'
              }
              originalPrice={
                benefit['plan']
                  ? formatPrice(benefit['plan']['originalCost'])
                  : '$0.00'
              }
              isOriginalPriceShown={shouldShowCurrentValue}
              priceStyleType={getPlanCostStyle(benefit['plan']['costType'])}
              originalPriceStyleType={getPlanCostStyle(
                benefit['plan']['costType']
              )}
              infoButton={
                !benefit['plan']['title'] ||
                benefit['plan']['title']?.includes('Contribution')
                  ? getButtonStyle(
                      benefit['alerts']
                        ? 'alert'
                        : benefit['plan']['detailsLink']
                        ? 'button'
                        : '',
                      benefit['alerts'],
                      getButtonModal(benefit['type'])
                    )
                  : ''
              }
              changeButton={
                <Button
                  size="sm"
                  variant="primary"
                  onClick={getButtonModal(benefit['type'])}
                >
                  Change
                </Button>
              }
            />
          ))}
          <HealthFsaModal
            showHealthFSAModal={showHealthFSAModal}
            handleCloseHealthFSAModal={handleCloseHealthFSAModal}
          />
          <DependantFsaModal
            showDependedFSAModal={showDependedFSAModal}
            handleCloseDependedFSAModal={handleCloseDependedFSAModal}
          />
        </Accordion>
      </Row>
      <Row className="mt-10">
        <Accordion
          title={'Life & Disability Insurance'}
          infoIcon={light('circle-exclamation')}
          className={'highlight-open'}
        >
          <Container className="custom-accordion-demo heading">
            <Row className="body-rows">
              <Col className="title-container col-lg-4">&nbsp;</Col>
              <Col className="avatars-col col-lg-2">&nbsp;</Col>
              <Col className="d-flex p-lg-0 col-lg-2">
                <Col className="price-col me-6 me-lg-0">
                  <span className="d-none d-lg-block text-light-grey-normal text-fs-6 fw-light text-nowrap">
                    {shouldShowCurrentValue ? 'Current Cost' : ''}
                  </span>
                </Col>
                <Col className="price-col">
                  <span className="text-light-grey-normal text-fs-6 fw-light text-nowrap">
                    New Cost
                  </span>
                </Col>
              </Col>
            </Row>
          </Container>
          {activeLifeAndDisabilityInsurances.map(
            (benefit: any, index: number) => {
              return (
                <AccordionBodyItem
                  key={index}
                  infoIcon={getCardIcon(
                    benefit['generic'] && benefit['generic']['icon']
                  )}
                  title={
                    !benefit['generic']
                      ? separateStringAndMakeFirstLetterUppercase(
                          benefit['type']
                        )
                      : benefit['generic']['name']
                  }
                  infoIconVariant={'icon-default'}
                  subtitle={
                    !benefit['plan']['id'].includes('noCoverage')
                      ? `${benefit['plan']['title']}${
                          benefit['plan']['coverage']
                            ? `, ${formatPrice(
                                benefit['plan']['coverage'],
                                'en-US',
                                {
                                  currency: 'USD',
                                  style: 'currency',
                                  minimumFractionDigits: 0,
                                }
                              )}`
                            : ''
                        }`
                      : 'No Coverage'
                  }
                  price={
                    benefit['plan']
                      ? formatPrice(benefit['plan']['cost'])
                      : '$0.00'
                  }
                  originalPrice={
                    benefit['plan']
                      ? formatPrice(benefit['plan']['originalCost'])
                      : '$0.00'
                  }
                  isOriginalPriceShown={shouldShowCurrentValue}
                  priceStyleType={getPlanCostStyle(
                    benefit['plan'] && benefit['plan']['costType']
                  )}
                  originalPriceStyleType={getPlanCostStyle(
                    benefit['plan'] && benefit['plan']['costType']
                  )}
                  infoButton={getButtonStyle(
                    benefit['alerts']
                      ? 'alert'
                      : !benefit['plan']
                      ? ''
                      : benefit['plan']['detailsLink']
                      ? 'button'
                      : '',
                    benefit['alerts'],
                    getButtonModal(benefit['type'])
                  )}
                  changeButton={
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={getButtonModal(benefit['type'])}
                    >
                      Change
                    </Button>
                  }
                />
              );
            }
          )}
          <BaseLifeInsuranceModal
            showBaseLifeInsuranceModal={showBaseLifeInsuranceModal}
            handleCloseBaseLifeInsuranceModal={
              handleCloseBaseLifeInsuranceModal
            }
          />
          <OptionalLifeInsuranceModal
            showOptionalLifeInsuranceModal={showOptionalLifeInsuranceModal}
            handleCloseOptionalLifeInsuranceModal={
              handleCloseOptionalLifeInsuranceModal
            }
          />
          <SpouseLifeInsuranceModal
            showSpouseLifeInsuranceModal={showSpouseLifeInsuranceModal}
            handleCloseSpouseLifeInsuranceModal={
              handleCloseSpouseLifeInsuranceModal
            }
          />
          <LongTermDisabilityServicesModal
            showLongTermDisabilityServicesModal={
              showLongTermDisabilityServicesModal
            }
            handleCloseLongTermDisabilityServicesModal={
              handleCloseLongTermDisabilityServicesModal
            }
          />
        </Accordion>
      </Row>
      <Row className="mt-10">
        <Accordion title={'Lifestyle Benefits'} className={'highlight-open'}>
          <Container className="custom-accordion-demo heading">
            <Row className="body-rows">
              <Col className="title-container col-lg-4">&nbsp;</Col>
              <Col className="avatars-col col-lg-2">&nbsp;</Col>
              <Col className="d-flex p-lg-0 col-lg-2">
                <Col className="price-col me-6 me-lg-0">
                  <span className="d-none d-lg-block text-light-grey-normal text-fs-6 fw-light text-nowrap">
                    {shouldShowCurrentValue ? 'Current Cost' : ''}
                  </span>
                </Col>
                <Col className="price-col">
                  <span className="text-light-grey-normal text-fs-6 fw-light text-nowrap">
                    New Cost
                  </span>
                </Col>
              </Col>
            </Row>
          </Container>
          {activeLifestyleBenefits.map((benefit: any, index: number) => (
            <AccordionBodyItem
              key={index}
              infoIcon={getCardIcon(
                benefit['generic'] && benefit['generic']['icon']
              )}
              title={
                !benefit['generic']
                  ? separateStringAndMakeFirstLetterUppercase(benefit['type'])
                  : benefit['generic']['title']
              }
              infoIconVariant={'icon-default'}
              subtitle={
                benefit['plan'] ? benefit['plan']['title'] : 'No Coverage'
              }
              price={
                benefit['plan'] ? formatPrice(benefit['plan']['cost']) : '$0.00'
              }
              originalPrice={
                benefit['plan']
                  ? formatPrice(benefit['plan']['originalCost'])
                  : '$0.00'
              }
              isOriginalPriceShown={shouldShowCurrentValue}
              priceStyleType={getPlanCostStyle(
                benefit['plan'] && benefit['plan']['costType']
              )}
              originalPriceStyleType={getPlanCostStyle(
                benefit['plan'] && benefit['plan']['costType']
              )}
              infoButton={
                !benefit['plan']['title'] ||
                benefit['plan']['title']?.includes('Coverage')
                  ? getButtonStyle(
                      benefit['alerts']
                        ? 'alert'
                        : !benefit['plan']
                        ? ''
                        : benefit['plan']['detailsLink']
                        ? 'button'
                        : '',
                      benefit['alerts'],
                      getButtonModal(benefit['type'])
                    )
                  : ''
              }
              changeButton={
                <Button
                  size="sm"
                  variant="primary"
                  onClick={getButtonModal(benefit['type'])}
                >
                  Change
                </Button>
              }
            />
          ))}
          <LegalServicesModal
            showLegalServicesModal={showLegalServicesModal}
            handleCloseLegalServicesModal={handleCloseLegalServicesModal}
          />
        </Accordion>
      </Row>
      <section className="mt-10 d-flex flex-column flex-lg-row justify-content-center">
        <Button
          className="text-nowrap order-2 order-lg-1 mt-7 mt-lg-0 me-lg-7"
          variant="primary-outline"
        >
          Cancel
        </Button>
        <Button
          onClick={() => setSubmitted(true)}
          className="text-nowrap order-1 order-lg-2"
          variant="primary"
        >
          Submit
        </Button>
      </section>
    </Container>
  );
}

function Dashboard() {
  const [submmited, setSubmitted] = useState(false);

  return (
    <EnrollmentProvider>
      <FamilyMembersProvider>
        {!submmited ? (
          <EnrollmentView setSubmitted={setSubmitted} />
        ) : (
          <EnrollmentSuccess setSubmitted={setSubmitted} />
        )}
      </FamilyMembersProvider>
    </EnrollmentProvider>
  );
}

export default Dashboard;
